import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  DndContext, MouseSensor, TouchSensor, useSensor, useSensors,
  closestCenter, DragOverlay, useDroppable, useDraggable,
} from "@dnd-kit/core";
import api from "@/lib/api";
import { STATUSES, cn, dateLocale } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Filter, CalendarDays, ChevronDown } from "lucide-react";
import RelanceButton from "@/components/RelanceButton";
import PlayerSheet from "@/components/PlayerSheet";
import AddPlayerDialog from "@/components/AddPlayerDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PipelineFilters, { applyPipelineFilters, DEFAULT_FILTERS } from "@/components/pipeline/PipelineFilters";
import { STAGE_STYLE } from "@/lib/stageColors";
import ConfirmPlacementDialog from "@/components/placements/ConfirmPlacementDialog";
import { fetchSports } from "@/lib/sportsCatalog";
import { sportFilterConfig } from "@/lib/sportFilters";
import { useLang, translateFr as tfr } from "@/i18n";

// Full 5-stage lifecycle board — Repéré → Contacté → En discussion → Mandat signé → Placé
const CORE_STAGES = ["Repéré", "Contacté", "En discussion", "Mandat signé", "Placé"];
const NEXT_STAGE = { "Repéré": "Contacté", "Contacté": "En discussion", "En discussion": "Mandat signé" };

const filtersFromParams = (sp) => {
  const status = sp.get("status");
  return {
    ...DEFAULT_FILTERS,
    sport: sp.get("sport") || "__all__",
    position: sp.get("position") || "__all__",
    status: status && STATUSES.includes(status) ? status : "__all__",
  };
};

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 639px)").matches : false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const fn = (e) => setMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return mobile;
}

export default function Pipeline() {
  const { t } = useLang();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sports, setSports] = useState([]);
  const [openPlayer, setOpenPlayer] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState("Repéré");
  const [q, setQ] = useState("");
  const [clubFilter, setClubFilter] = useState("__all__");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [activeId, setActiveId] = useState(null);
  const [clubsMap, setClubsMap] = useState({});
  const [overdueMap, setOverdueMap] = useState({});
  const [mobileStage, setMobileStage] = useState(CORE_STAGES[0]);
  const [confirmPlayer, setConfirmPlayer] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const next = filtersFromParams(searchParams);
    setFilters((f) =>
      f.sport === next.sport && f.position === next.position && f.status === next.status
        ? f : { ...f, sport: next.sport, position: next.position, status: next.status }
    );
  }, [searchParams]);

  const applyFilters = useCallback((next) => {
    setFilters(next);
    setSearchParams((sp) => {
      const p = new URLSearchParams(sp);
      const setOrDel = (k, v, def) => { if (v && v !== def) p.set(k, v); else p.delete(k); };
      setOrDel("sport", next.sport, "__all__");
      setOrDel("position", next.position, "__all__");
      setOrDel("status", next.status, "__all__");
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => { fetchSports().then(setSports).catch(() => {}); }, []);

  useEffect(() => {
    if (!sports.length || filters.position === "__all__") return;
    const config = sportFilterConfig(sports, filters.sport);
    if (!config || !config.options.some((o) => o.id === filters.position)) {
      applyFilters({ ...filters, position: "__all__" });
    }
  }, [sports, filters, applyFilters]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 350, tolerance: 8 } }),
  );

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const r = await api.get("/players");
      setPlayers(r.data);
    } catch (e) {
      console.error("pipeline load failed", e);
      setPlayers((prev) => { if (!prev.length) setLoadError(true); return prev; });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/clubs").then((r) => {
      const m = {};
      r.data.forEach((c) => (m[c.name] = c));
      setClubsMap(m);
    }).catch(() => {});
  }, [load]);

  const clubs = useMemo(() => {
    const counts = {};
    players.forEach((p) => { if (p.club) counts[p.club] = (counts[p.club] || 0) + 1; });
    return Object.keys(counts).sort().map((name) => ({ name, count: counts[name] }));
  }, [players]);

  const filtered = useMemo(() => {
    let arr = players;
    if (q.trim()) {
      const t = q.toLowerCase();
      arr = arr.filter((p) =>
        [p.name, p.position, p.club, p.sport, p.competition, p.nationality]
          .some((v) => (v || "").toLowerCase().includes(t)));
    }
    if (clubFilter !== "__all__") arr = arr.filter((p) => p.club === clubFilter);
    arr = applyPipelineFilters(arr, filters, sports);
    return arr;
  }, [players, q, clubFilter, filters, sports]);

  const placedCount = useMemo(() => players.filter((p) => p.status === "Placé").length, [players]);
  const stages = CORE_STAGES;

  const cols = useMemo(() => {
    const m = {};
    stages.forEach((s) => (m[s] = []));
    filtered.forEach((p) => { if (m[p.status]) m[p.status].push(p); });
    return m;
  }, [filtered, stages]);

  const onDragStart = (e) => {
    setActiveId(e.active.id);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(12); } catch { /* not supported */ }
    }
  };

  const moveTo = useCallback(async (playerId, newStatus) => {
    const player = players.find((p) => p.id === playerId);
    if (!player || player.status === newStatus) return;
    const prev = player.status;
    setPlayers((arr) => arr.map((p) => (p.id === playerId ? { ...p, status: newStatus } : p)));
    try {
      await api.patch(`/players/${playerId}/status`, { status: newStatus });
      toast.success(`${t("pipeline.movedTo")} ${t(`status.${newStatus}`)}`);
      load(); // refresh canonical follow-up state (new cadence) from the backend
    } catch {
      toast.error(t("pipeline.stageError"));
      setPlayers((arr) => arr.map((p) => (p.id === playerId ? { ...p, status: prev } : p)));
    }
  }, [players, t, load]);

  const onDragEnd = async (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const newStatus = STATUSES.includes(over.id) ? over.id : players.find((p) => p.id === over.id)?.status;
    if (newStatus) moveTo(active.id, newStatus);
  };

  const activePlayer = players.find((p) => p.id === activeId);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => filters[k] !== DEFAULT_FILTERS[k]).length;
  const resetAll = () => { setQ(""); setClubFilter("__all__"); applyFilters(DEFAULT_FILTERS); };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-5 max-w-[1500px] mx-auto space-y-4 animate-fade-in">
      {/* Header */}
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-medium">Pipeline</div>
        <h1 className="text-3xl md:text-[34px] font-bold tracking-tight mt-1" data-testid="pipeline-count">
          {t("pipeline.athleteCount", { n: filtered.length, s: filtered.length > 1 ? "s" : "" })}
          {filtered.length !== players.length && (
            <span className="text-base text-muted-foreground/60 font-normal ml-2">{t("pipeline.of", { n: players.length })}</span>
          )}
        </h1>
      </header>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("pipeline.search")}
          className="pl-10 h-11 rounded-xl border-border bg-card text-sm shadow-sm"
          data-testid="pipeline-search-input"
        />
      </div>

      {/* Toolbar: club selector (wide) · Filtres · + Athlète */}
      <div className="flex items-center gap-2">
        <Select value={clubFilter} onValueChange={setClubFilter}>
          <SelectTrigger className="h-11 flex-1 rounded-xl border-border bg-card shadow-sm [&>span]:flex-1 [&>span]:text-left" data-testid="pipeline-club-filter">
            <Filter className="h-3.5 w-3.5 mr-2 text-muted-foreground shrink-0" />
            <SelectValue placeholder={t("pipeline.allClubs")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t("pipeline.allClubs")}</SelectItem>
            {clubs.map((c) => (
              <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => setFiltersOpen((o) => !o)}
          className={cn("h-11 rounded-xl gap-1.5 px-4 bg-card border-border shadow-sm",
            filtersOpen && "border-athlynx/40 text-athlynx")}
          data-testid="pipeline-filters-btn"
          aria-expanded={filtersOpen}
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">
            {t("pipeline.filters")}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </span>
          <ChevronDown className={cn("h-3.5 w-3.5 opacity-60 transition-transform", filtersOpen && "rotate-180")} />
        </Button>
        <Button
          onClick={() => { setDefaultStatus("Repéré"); setAddOpen(true); }}
          className="h-11 px-4 md:px-5 rounded-xl bg-athlynx hover:bg-athlynx/90 text-white gap-1.5 shadow-sm font-semibold"
          data-testid="pipeline-add-player-btn"
        >
          <Plus className="h-4 w-4" /> {t("pipeline.athlete")}
        </Button>
      </div>

      {/* Advanced filters (toggled from toolbar) */}
      {filtersOpen && (
        <PipelineFilters
          filters={filters}
          onChange={applyFilters}
          onReset={() => applyFilters(DEFAULT_FILTERS)}
          resultCount={filtered.length}
          totalCount={players.length}
          statuses={CORE_STAGES}
          sports={sports}
          hideHeader
        />
      )}

      {/* Mobile stage tabs */}
      {!loading && !loadError && filtered.length > 0 && (
        <div className="flex sm:hidden gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="pipeline-mobile-tabs">
          {stages.map((s) => (
            <button key={s} onClick={() => setMobileStage(s)} data-testid={`mobile-stage-${s}`}
              className={cn("shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors",
                mobileStage === s ? "border-athlynx/40 bg-athlynx/10 text-athlynx" : "border-border bg-card text-muted-foreground")}>
              <span className={cn("h-1.5 w-1.5 rounded-full", STAGE_STYLE[s].dot)} />
              {t(`status.${s}`)} ({cols[s]?.length ?? 0})
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline-loading">
          {CORE_STAGES.map((s) => (
            <div key={s} className="rounded-2xl border border-border bg-card/50 p-3 space-y-3 min-h-[320px]">
              <div className="h-5 w-24 rounded bg-secondary animate-pulse" />
              {[0, 1, 2].map((i) => <div key={i} className="h-[92px] rounded-xl bg-secondary/60 animate-pulse" />)}
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div className="rounded-2xl border border-border bg-card px-6 py-14 text-center" data-testid="pipeline-error-state">
          <h2 className="text-base font-semibold">Impossible de charger la Pipeline.</h2>
          <Button variant="outline" className="mt-5 h-10" onClick={() => { setLoading(true); load(); }} data-testid="pipeline-retry">
            {tfr("Réessayer")}
          </Button>
        </div>
      ) : filtered.length === 0 && players.length > 0 ? (
        <div className="rounded-2xl border border-border bg-card px-6 py-14 text-center" data-testid="pipeline-empty-state">
          <Search className="h-8 w-8 mx-auto text-muted-foreground/40" />
          <h2 className="text-base md:text-lg font-semibold mt-4">{tfr("Aucun athlète ne correspond à ces critères.")}</h2>
          <Button variant="outline" className="mt-5 h-11" onClick={resetAll} data-testid="pipeline-empty-reset">
            <Filter className="h-4 w-4 mr-2" /> Réinitialiser les filtres
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          {!isMobile ? (
            <div className="grid gap-3 pb-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {stages.map((s) => (
                <Column key={s} status={s} players={cols[s]} overdueMap={overdueMap}
                  onAdd={() => { setDefaultStatus(s); setAddOpen(true); }}
                  onClick={(id) => setOpenPlayer(id)} clubsMap={clubsMap} onLogged={load}
                  onConfirmPlacement={setConfirmPlayer} onMove={moveTo} />
              ))}
            </div>
          ) : (
            <div className="pb-4">
              <Column status={mobileStage} players={cols[mobileStage] || []} overdueMap={overdueMap}
                onAdd={() => { setDefaultStatus(mobileStage); setAddOpen(true); }}
                onClick={(id) => setOpenPlayer(id)} clubsMap={clubsMap} onLogged={load} mobile
                onConfirmPlacement={setConfirmPlayer} onMove={moveTo} />
            </div>
          )}
          <DragOverlay>
            {activePlayer ? <KanbanCard player={activePlayer} overdue={overdueMap[activePlayer.id]} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <PlayerSheet playerId={openPlayer} open={!!openPlayer} onOpenChange={(o) => !o && setOpenPlayer(null)} onUpdated={load} />
      <AddPlayerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} defaultStatus={defaultStatus} />
      <ConfirmPlacementDialog open={!!confirmPlayer} onOpenChange={(o) => !o && setConfirmPlayer(null)}
        player={confirmPlayer} onConfirmed={load} />

      {/* Restrained post-placement entry point */}
      {!loading && !loadError && placedCount > 0 && (
        <div className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground pt-1" data-testid="pipeline-placements-entry">
          <span>{placedCount} {placedCount > 1 ? t("pipeline.placementsDone") : t("pipeline.placementDone")}</span>
          <Link to="/placements" className="font-medium text-foreground/80 hover:text-athlynx transition-colors"
            data-testid="pipeline-placements-link">
            {t("pipeline.seePlacements")} →
          </Link>
        </div>
      )}
    </div>
  );
}

function Column({ status, players, onAdd, onClick, clubsMap, onLogged, overdueMap, mobile, onConfirmPlacement, onMove }) {
  const { t } = useLang();
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const style = STAGE_STYLE[status] || STAGE_STYLE["Repéré"];
  return (
    <div
      ref={setNodeRef}
      className={cn("rounded-2xl border flex flex-col min-h-[340px] transition-colors duration-150",
        isOver ? style.over : style.col)}
      data-testid={`column-${status}`}
    >
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn("h-2 w-2 rounded-full shrink-0", style.dot)} />
          <span className="text-[13px] font-semibold truncate">{t(`status.${status}`)}</span>
          <span className="text-[11px] font-semibold tabular-nums rounded-md bg-black/[0.05] dark:bg-secondary/50 px-1.5 py-0.5 text-muted-foreground">
            {players.length}
          </span>
        </div>
        <button onClick={onAdd} className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-black/[0.05] dark:hover:bg-secondary/60 transition-colors"
          data-testid={`column-add-${status}`} aria-label={`${tfr("Ajouter un athlète")} — ${tfr(status)}`}>
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className={cn("flex-1 px-2 pb-2 space-y-2 overflow-y-auto scroll-thin",
        !mobile && "max-h-[calc(100vh-330px)]")}>
        {players.map((p) => (
          <DraggableCard key={p.id} player={p} overdue={overdueMap?.[p.id]}
            onClick={() => onClick(p.id)} club={clubsMap[p.club]} onLogged={onLogged}
            onConfirmPlacement={onConfirmPlacement} onMove={onMove} />
        ))}
        {players.length === 0 && (
          status === "Placé" ? (
            <div className="rounded-xl border border-dashed border-black/[0.12] dark:border-border py-8 px-3 text-center text-xs text-muted-foreground"
              data-testid="column-empty-place">
              {tfr("Aucun athlète placé pour le moment.")}
            </div>
          ) : (
          <button onClick={onAdd} data-testid={`column-empty-add-${status}`}
            className="w-full rounded-xl border border-dashed border-black/[0.12] dark:border-border py-8 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground hover:border-black/[0.25] dark:hover:border-foreground/25 transition-colors">
            <span className="h-9 w-9 rounded-full border border-current/30 flex items-center justify-center">
              <Plus className="h-4 w-4" />
            </span>
            <span className="text-xs">{tfr("Ajouter un athlète")}</span>
          </button>
          )
        )}
      </div>
    </div>
  );
}

function DraggableCard({ player, onClick, club, onLogged, overdue, onConfirmPlacement, onMove }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: player.id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => { if (!isDragging) onClick(); }}
      style={{ touchAction: "pan-y" }}
      className={cn("select-none", isDragging && "opacity-30 scale-[1.02] shadow-2xl ring-1 ring-foreground/20")}
      data-testid={`kanban-card-${player.id}`}
    >
      <KanbanCard player={player} club={club} onLogged={onLogged} overdue={overdue}
        onConfirmPlacement={onConfirmPlacement} onMove={onMove} />
    </div>
  );
}

function followUpDisplay(player) {
  const fu = player.follow_up;
  if (!fu || !fu.next_follow_up_date) return { label: "—", cls: "text-muted-foreground" };
  if (fu.state === "OVERDUE") return { label: `${tfr("En retard de")} ${fu.days_overdue ?? ""} ${tfr("j")}`, cls: "text-athlynx font-medium" };
  if (fu.state === "DUE_TODAY") return { label: tfr("À relancer aujourd'hui"), cls: "text-warning font-medium" };
  const d = new Date(fu.next_follow_up_date + "T12:00:00");
  return {
    label: `${tfr("Prochaine relance :")} ${d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })}`,
    cls: "text-muted-foreground",
  };
}

function KanbanCard({ player, club, onLogged, dragging, overdue, onConfirmPlacement, onMove }) {
  const { t, tf } = useLang();
  const style = STAGE_STYLE[player.status] || STAGE_STYLE["Repéré"];
  const fu = followUpDisplay(player);
  const next = NEXT_STAGE[player.status];
  return (
    <div
      className={cn(
        "kanban-card rounded-xl border bg-white border-black/[0.08] shadow-sm p-3 transition-colors cursor-pointer",
        "dark:bg-secondary/50 dark:border-border dark:shadow-none",
        "hover:border-black/[0.2] dark:hover:border-foreground/25",
        dragging && "shadow-xl scale-105"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{player.name}</div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {[player.position, player.club, player.age ? `${player.age}a` : null].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <span className={cn("h-2 w-2 rounded-full mt-1.5 shrink-0", style.dot)} />
      </div>
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-black/[0.05] dark:border-border">
        <span className={cn("inline-flex items-center gap-1.5 text-[11px] min-w-0", fu.cls)}
          data-testid={`card-followup-${player.id}`}>
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{tf(fu.label)}</span>
        </span>
        <RelanceButton player={player} club={club} onLogged={onLogged} />
      </div>
      {next && (
        <div className={cn("mt-2 grid gap-1.5", player.status === "En discussion" ? "grid-cols-2" : "grid-cols-1")}>
          {player.status === "En discussion" && (
            <button
              onClick={(e) => { e.stopPropagation(); window.location.assign(`/discussions-actives?athlete=${player.id}`); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="rounded-lg border border-info/30 bg-info/[0.06] text-info hover:bg-info/[0.12] text-[11px] font-medium py-1.5 transition-colors"
              data-testid={`open-discussion-${player.id}`}
            >
              {t("pipeline.openDiscussion")}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onMove?.(player.id, next); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="rounded-lg border border-border bg-black/[0.02] dark:bg-secondary/30 text-foreground/80 hover:text-foreground hover:bg-black/[0.05] dark:hover:bg-secondary/60 text-[11px] font-medium py-1.5 transition-colors truncate px-1"
            data-testid={`move-next-${player.id}`}
          >
            {tf("Passer à")} {t(`status.${next}`)}
          </button>
        </div>
      )}
      {player.status === "Mandat signé" && onConfirmPlacement && (
        <button
          onClick={(e) => { e.stopPropagation(); onConfirmPlacement(player); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="mt-2 w-full rounded-lg border border-success/30 bg-success/[0.06] text-success hover:bg-success/[0.12] text-[11px] font-medium py-1.5 transition-colors"
          data-testid={`confirm-placement-${player.id}`}
        >
          {t("pipeline.confirmPlacement")}
        </button>
      )}
      {player.status === "Placé" && (
        <button
          onClick={(e) => { e.stopPropagation(); window.location.assign(`/placements?athlete=${player.id}`); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="mt-2 w-full rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/[0.12] text-[11px] font-medium py-1.5 transition-colors"
          data-testid={`open-placement-${player.id}`}
        >
          {tf("Ouvrir le suivi")}
        </button>
      )}
    </div>
  );
}
